/**
 * Carbon overflow menu for row and card actions: a square kebab button that
 * opens a layer-02 menu (headless UI Menu: arrow keys, type-ahead, Escape)
 * with the menu shadow and `data-menu`. Items are 40px in secondary text,
 * turning layer-hover on hover or focus. A danger item (a destructive action)
 * sits after a divider in Coral ink.
 *
 * The menu renders through a headless UI Portal at a fixed position taken
 * from the trigger (useAnchoredPosition), so a row action inside the
 * DataTable's horizontal scroller is never clipped. It opens under the
 * trigger, aligned to its end by default, and flips above it near the bottom
 * of the viewport.
 */
import { Fragment, useState, type ReactNode } from 'react'
import { Menu, Portal } from '@headlessui/react'
import clsx from 'clsx'
import { IconCheck, IconOverflow } from './icons'
import { useAnchoredPosition } from './useAnchoredPosition'

export interface OverflowMenuItem {
  id: string
  label: ReactNode
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  /**
   * A selectable item (a MenuButton choosing one value): the chosen one shows Carbon's check and says
   * "selected". Once any item sets it, every item gets the 16px check column so labels align.
   */
  selected?: boolean
}

export interface OverflowMenuProps {
  /** The trigger's accessible name ("Actions for Litware Health"). */
  label: string
  items: OverflowMenuItem[]
  /** Trigger size: sm 32px, md 40px (default), lg 48px. */
  size?: 'sm' | 'md' | 'lg'
  align?: 'start' | 'end'
  className?: string
}

const TRIGGER = { sm: 'h-8 w-8', md: 'h-10 w-10', lg: 'h-12 w-12' } as const

/** The layer-02 menu itself, shared by OverflowMenu, MenuButton and the BatchActionBar's "More actions". */
export function MenuPanel({ anchor, align, items }: { anchor: HTMLElement | null; align: 'start' | 'end'; items: OverflowMenuItem[] }) {
  const [panel, setPanel] = useState<HTMLElement | null>(null)
  const style = useAnchoredPosition(anchor, panel, true, { side: 'bottom', align })
  const selectable = items.some((item) => item.selected !== undefined)
  return (
    <Menu.Items
      ref={setPanel}
      data-menu=""
      style={style}
      className="z-[70] min-w-[10rem] max-w-[min(20rem,calc(100vw-32px))] bg-layer-2 text-text-primary shadow-menu focus:outline-none"
    >
      {items.map((item, i) => (
        <Fragment key={item.id}>
          {item.danger && i > 0 && !items[i - 1].danger ? <div role="separator" className="h-px bg-line-subtle" /> : null}
          <Menu.Item disabled={item.disabled}>
            {({ active, disabled }) => (
              <button
                type="button"
                onClick={item.onClick}
                className={clsx(
                  'flex h-10 w-full items-center gap-2 whitespace-nowrap px-4 text-left text-body transition-colors duration-110 ease-carbon',
                  disabled
                    ? 'cursor-not-allowed text-text-placeholder'
                    : item.danger
                      ? active
                        ? 'bg-crit-ink text-[color:var(--text-inverse)]'
                        : 'text-crit-ink'
                      : active
                        ? 'bg-layer-hover text-text-primary'
                        : item.selected
                          ? 'text-text-primary'
                          : 'text-text-secondary',
                )}
              >
                {selectable ? (
                  <span aria-hidden="true" className="grid h-4 w-4 flex-none place-items-center">
                    {item.selected ? <IconCheck /> : null}
                  </span>
                ) : null}
                {item.label}
                {item.selected ? <span className="sr-only">, selected</span> : null}
              </button>
            )}
          </Menu.Item>
        </Fragment>
      ))}
    </Menu.Items>
  )
}

export function OverflowMenu({ label, items, size = 'md', align = 'end', className }: OverflowMenuProps) {
  const [trigger, setTrigger] = useState<HTMLElement | null>(null)
  return (
    <Menu as="div" className={clsx('relative inline-flex flex-none', className)}>
      {({ open }) => (
        <>
          <Menu.Button
            ref={setTrigger}
            aria-label={label}
            className={clsx(
              'grid place-items-center text-[color:var(--icon-secondary)] transition-colors duration-110 ease-carbon',
              'hover:bg-layer-hover hover:text-text-primary aria-expanded:bg-layer-2 aria-expanded:text-text-primary',
              TRIGGER[size],
            )}
          >
            <IconOverflow />
          </Menu.Button>
          {open ? (
            <Portal>
              <MenuPanel anchor={trigger} align={align} items={items} />
            </Portal>
          ) : null}
        </>
      )}
    </Menu>
  )
}

export default OverflowMenu
